import { cn, getPaymentBehavior } from "@/lib/utils";
import { PaymentBehavior } from "@/networking/payments.api";
import { createAddressCountryReset } from "@/lib/address-country-selection";
import {
  resolvePaymentMethodDisplayLabel,
  resolvePaymentMethodLogoUrl,
} from "@/lib/payment-method-display";
import {
  CreditCardFormData,
  createCreditCardFormDataSchema,
  CustomerPaymentMethod,
  CustomerPaymentMethodsResponse,
} from "@/networking/payments.api";
import {
  customerPaymentMethodsQueryKey,
  useAddCreditCardMutation,
  useDeletePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
} from "@/hooks/useCustomerPaymentMethods";
import useJwt from "@/components/checkout/hooks/useJwt";
import { VgsCollectSubmitResponse } from "@/networking/vault.api";
import { CartResponse } from "@/networking/carts.api";
import {
  CountriesAndLanguages,
  updateAddress,
} from "@/networking/addresses.api";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { useDynamicAddressResolver } from "@/hooks/useAddressFields";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  useForm,
  Control,
  Path,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
} from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";
import CreditCardDialog from "./credit-card-dialog";
import DeleteDialog from "./confirm-action-dialog";
import EllipsesDropdown from "./ellipses-dropdown";
import ViewPaymentMethodDialog from "../management/view-payment-method-dialog";
import EditPaymentMethodDialog, {
  EditPaymentMethodFormData,
} from "../management/edit-payment-method-dialog";
import RadioInputBar from "./radio-input-bar";
import { AvailablePaymentMethod } from "@/networking/carts.api";
import AlternatePaymentIcons from "./alternate-payment-icons";
import { useVgs } from "@/hooks/useVgs";
import { isApiError, type ApiError } from "@/lib/api-helpers";
import { sanitizeErrorMessage } from "@/lib/error-sanitizer";
import DropZone from "../drop-zones/drop-zone";
import CustomerDetailsForm from "@/components/checkout/customer-details-form";
import { useVgsPayment } from "@/hooks/useVgsPayment";
import Checkbox from "../ui/input/checkbox";
import AddressForm from "./address-form";
import useCartCountry from "./hooks/useCartCountry";
import useAutocomplete from "./hooks/useAutocomplete";
import type { CheckoutFormData } from "./checkout.types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const VGS_SESSION_TIMEOUT = 5 * 60 * 1000;

const RenderSelectedPaymentMethod = ({
  defaultPaymentMethod,
  customerPaymentMethodsLoading,
  customerPaymentMethods,
  selectedAlternatePaymentMethod,
  selectedPaymentMethodId,
}: {
  defaultPaymentMethod: CustomerPaymentMethod | null;
  customerPaymentMethodsLoading: boolean;
  customerPaymentMethods: CustomerPaymentMethod[];
  selectedAlternatePaymentMethod: AvailablePaymentMethod | null;
  selectedPaymentMethodId: number | null;
}) => {
  if (customerPaymentMethodsLoading) {
    return <div className="h-6 w-full animate-pulse rounded bg-gray-200"></div>;
  }

  if (!selectedPaymentMethodId && !selectedAlternatePaymentMethod) return null;

  const displayPaymentMethod =
    customerPaymentMethods?.find(
      (paymentMethod) => paymentMethod.id === selectedPaymentMethodId,
    ) ?? defaultPaymentMethod;

  if (!selectedAlternatePaymentMethod && !displayPaymentMethod) return null;

  const displayPaymentLogoUrl = displayPaymentMethod
    ? resolvePaymentMethodLogoUrl({
        logoUrl: displayPaymentMethod.details.logo_url,
        cardBrand: displayPaymentMethod.details.card_brand,
        cardType: displayPaymentMethod.details.card_type,
        detailsType: displayPaymentMethod.details.type,
        paymentTitle: displayPaymentMethod.payment_title,
        paymentType: displayPaymentMethod.payment_type,
        source: displayPaymentMethod.source,
      })
    : null;
  const displayPaymentLabel = displayPaymentMethod
    ? resolvePaymentMethodDisplayLabel({
        lastFour: displayPaymentMethod.details.last_four,
        cardBrand: displayPaymentMethod.details.card_brand,
        cardType: displayPaymentMethod.details.card_type,
        detailsType: displayPaymentMethod.details.type,
        paymentTitle: displayPaymentMethod.payment_title,
        paymentType: displayPaymentMethod.payment_type,
        source: displayPaymentMethod.source,
      })
    : null;

  return (
    <>
      {!selectedAlternatePaymentMethod ? (
        <div className="flex flex-row space-x-2">
          {displayPaymentLogoUrl && (
            <img
              src={displayPaymentLogoUrl}
              alt="payment card"
              className="h-5 w-10 object-contain"
            />
          )}
          {displayPaymentLabel && (
            <div className="flex flex-col">
              <div className="text-sm font-medium text-gray-900">
                {displayPaymentLabel}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-row space-x-2">
          <AlternatePaymentIcons
            paymentMethod={selectedAlternatePaymentMethod}
          />
          <div className="text-sm font-medium text-gray-900">
            {selectedAlternatePaymentMethod?.name}
          </div>
        </div>
      )}
    </>
  );
};

export default function CustomerPaymentMethods({
  customerPaymentMethods,
  customerPaymentMethodsLoading,
  selectedPaymentMethodId,
  handleSelect,
  // jwt prop kept for caller compatibility; auth is now read inside the
  // useCustomerPaymentMethods hooks via useJwt(), so this is unused locally.
  jwt: _jwt,
  cartToken,
  defaultPaymentMethod,
  networkError,
  alternatePaymentMethods,
  setSelectedAlternatePaymentMethod,
  selectedAlternatePaymentMethod,
  query,
  isPaymentMethodMutationPending = false,
  disabled = false,
  onCustomerDetailsChange,
  customerDetailsValidationErrors,
  onClearCustomerDetailsFieldError,
  customerDetailsInitialValues,
  checkoutFormControl,
  checkoutFormWatch,
  checkoutFormSetValue,
  checkoutFormClearErrors,
  countries,
  paymentMethodsIncludes,
}: {
  customerPaymentMethods: CustomerPaymentMethod[];
  customerPaymentMethodsLoading: boolean;
  selectedPaymentMethodId: number | null;
  handleSelect: (paymentMethod: CustomerPaymentMethod) => void | Promise<void>;
  jwt: string;
  cartToken: string;
  defaultPaymentMethod: CustomerPaymentMethod | null;
  networkError: string | undefined;
  alternatePaymentMethods: AvailablePaymentMethod[];
  setSelectedAlternatePaymentMethod: (
    paymentMethod: AvailablePaymentMethod | null,
  ) => void;
  selectedAlternatePaymentMethod: AvailablePaymentMethod | null;
  query: UseQueryResult<CartResponse | null>;
  isPaymentMethodMutationPending?: boolean;
  disabled?: boolean;
  onCustomerDetailsChange?: (details: Record<string, string> | null) => void;
  customerDetailsValidationErrors?: Record<string, string[]>;
  onClearCustomerDetailsFieldError?: (fieldKey: string) => void;
  customerDetailsInitialValues?: Record<string, string>;
  checkoutFormControl?: Control<CheckoutFormData>;
  checkoutFormWatch?: UseFormWatch<CheckoutFormData>;
  checkoutFormSetValue?: UseFormSetValue<CheckoutFormData>;
  checkoutFormClearErrors?: UseFormClearErrors<CheckoutFormData>;
  countries?: CountriesAndLanguages["countries"];
  paymentMethodsIncludes?: string | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getPaymentAccountId } = useVgs(query);
  const { subdomain } = query.data?.cart?.company || {};
  const isDisabled = isPaymentMethodMutationPending || disabled;
  // Radio selection stays clickable while a previous selection is still
  // settling — the optimistic UI reflects the latest click and the new
  // mutation supersedes the in-flight one.
  const isRadioDisabled = disabled;
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedCreditCard, setSelectedCreditCard] =
    useState<CustomerPaymentMethod | null>(null);
  const [openCreditCardDialog, setOpenCreditCardDialog] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<CustomerPaymentMethod | null>(null);
  const [editError, setEditError] = useState<string | undefined>(undefined);
  const [accordionValue, setAccordionValue] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const creditCardOption = query.data?.cart?.available_payment_methods?.find(
    (method: AvailablePaymentMethod) =>
      getPaymentBehavior(method.type) === PaymentBehavior.CREDIT_CARD,
  );
  const hasCreditCardOption = !!creditCardOption;

  const shouldDefaultToAlternatePayments =
    (!hasCreditCardOption || customerPaymentMethods.length === 0) &&
    alternatePaymentMethods?.length > 0;

  const [showAlternatePaymentMethods, setShowAlternatePaymentMethods] =
    useState(false);
  const [hasInitializedPaymentView, setHasInitializedPaymentView] =
    useState(false);
  const prevHasCreditCardOption = useRef<boolean | null>(null);
  const lastSyncedCardIdRef = useRef<number | null>(null);
  const [optimisticSelectedCardId, setOptimisticSelectedCardId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (
      optimisticSelectedCardId !== null &&
      selectedPaymentMethodId === optimisticSelectedCardId
    ) {
      setOptimisticSelectedCardId(null);
    }
  }, [optimisticSelectedCardId, selectedPaymentMethodId]);

  // A card is usable only if it has a complete billing address AND isn't
  // closed. Unusable cards are hidden from the list and skipped in the
  // cart-derived fallback (but not in the optimistic branch — explicit
  // user clicks still win visually).
  const isCardUsable = (card: CustomerPaymentMethod): boolean => {
    if (card.au_status === "closed") return false;
    const addr = card.billing_address;
    if (!addr) return false;
    return Boolean(
      addr.address1 && addr.city && addr.state && addr.zip && addr.country_code,
    );
  };

  // Filter to usable cards and render the default first.
  const displayedPaymentMethods = useMemo(() => {
    if (!customerPaymentMethods?.length) return customerPaymentMethods;
    const usable = customerPaymentMethods.filter(isCardUsable);
    const defaultIdx = usable.findIndex((c) => c.default);
    if (defaultIdx <= 0) return usable;
    const defaultCard = usable[defaultIdx];
    if (!defaultCard) return usable;
    return [defaultCard, ...usable.filter((_, i) => i !== defaultIdx)];
  }, [customerPaymentMethods]);

  // fallback: optimistic pick → selected card → default card → first usable card
  const effectiveSelectedCardId = useMemo(() => {
    // Explicit user clicks always win visually — only skip if closed.
    if (optimisticSelectedCardId !== null) {
      const optimisticCard = customerPaymentMethods.find(
        (card) => card.id === optimisticSelectedCardId,
      );
      if (optimisticCard && optimisticCard.au_status !== "closed") {
        return optimisticSelectedCardId;
      }
    }
    const matchedCard = customerPaymentMethods.find(
      (card) => card.id === selectedPaymentMethodId,
    );
    if (matchedCard && isCardUsable(matchedCard))
      return selectedPaymentMethodId;
    if (defaultPaymentMethod && isCardUsable(defaultPaymentMethod))
      return defaultPaymentMethod.id;
    const firstUsable = customerPaymentMethods.find(isCardUsable);
    return firstUsable?.id ?? null;
  }, [
    optimisticSelectedCardId,
    selectedPaymentMethodId,
    customerPaymentMethods,
    defaultPaymentMethod,
  ]);

  // set initial payment view
  useEffect(() => {
    if (
      !hasInitializedPaymentView &&
      !customerPaymentMethodsLoading &&
      query.data?.cart
    ) {
      if (shouldDefaultToAlternatePayments) {
        setShowAlternatePaymentMethods(true);
        if (
          !selectedAlternatePaymentMethod &&
          alternatePaymentMethods?.length > 0
        ) {
          setSelectedAlternatePaymentMethod(alternatePaymentMethods[0] ?? null);
        }
      }
      setHasInitializedPaymentView(true);
    }
  }, [
    hasInitializedPaymentView,
    customerPaymentMethodsLoading,
    query.data?.cart,
    shouldDefaultToAlternatePayments,
    selectedAlternatePaymentMethod,
    alternatePaymentMethods,
    setSelectedAlternatePaymentMethod,
  ]);

  // switch payment view on country change (only when hasCreditCardOption actually changes)
  useEffect(() => {
    if (!customerPaymentMethodsLoading && query.data?.cart) {
      const prevValue = prevHasCreditCardOption.current;
      const hasChanged =
        prevValue !== null && prevValue !== hasCreditCardOption;

      if (hasChanged) {
        // credit card became unavailable → switch to APMs and select first APM
        if (!hasCreditCardOption && alternatePaymentMethods?.length > 0) {
          setShowAlternatePaymentMethods(true);
          setSelectedAlternatePaymentMethod(alternatePaymentMethods[0] ?? null);
        }
        // credit card became available with saved cards → switch to credit card
        if (hasCreditCardOption && customerPaymentMethods.length > 0) {
          setShowAlternatePaymentMethods(false);
          setSelectedAlternatePaymentMethod(null);
          lastSyncedCardIdRef.current = null;
        }
      }

      prevHasCreditCardOption.current = hasCreditCardOption;
    }
  }, [
    hasCreditCardOption,
    alternatePaymentMethods,
    customerPaymentMethodsLoading,
    customerPaymentMethods.length,
    query.data?.cart,
    setSelectedAlternatePaymentMethod,
  ]);

  // sync cart when displayed card differs from cart's payment method
  useEffect(() => {
    if (
      !showAlternatePaymentMethods &&
      !customerPaymentMethodsLoading &&
      !isPaymentMethodMutationPending &&
      optimisticSelectedCardId === null &&
      effectiveSelectedCardId &&
      effectiveSelectedCardId !== selectedPaymentMethodId &&
      effectiveSelectedCardId !== lastSyncedCardIdRef.current &&
      customerPaymentMethods.length > 0
    ) {
      const cardToSelect = customerPaymentMethods.find(
        (card) => card.id === effectiveSelectedCardId,
      );
      if (cardToSelect) {
        lastSyncedCardIdRef.current = effectiveSelectedCardId;
        handleSelect(cardToSelect);
      }
    }
  }, [
    showAlternatePaymentMethods,
    customerPaymentMethodsLoading,
    isPaymentMethodMutationPending,
    optimisticSelectedCardId,
    effectiveSelectedCardId,
    selectedPaymentMethodId,
    customerPaymentMethods,
    handleSelect,
  ]);

  const [saveCardError, setSaveCardError] = useState<string | undefined>(
    undefined,
  );
  const [deleteCardError, setDeleteCardError] = useState<string | undefined>(
    undefined,
  );

  const resetVgsRef = useRef<() => void>(() => {});

  // Mirror the canonical key from useCustomerPaymentMethodsQuery so optimistic
  // writes target the same cache entry as the parent query in logged-in-
  // checkout.tsx.
  const { jwt: jwtFromHook } = useJwt();
  const countryIso = query.data?.cart?.country?.iso;
  const paymentMethodsQueryKey = customerPaymentMethodsQueryKey(
    jwtFromHook,
    countryIso,
  );

  // billing address state for alternate payment methods
  const useShippingForBilling = checkoutFormWatch?.(
    "customer.payment_method.use_shipping_address_for_billing",
  );
  const shippingCountryCode = checkoutFormWatch?.(
    "customer.shipping_address.country_code",
  );
  const shippingCountryName = checkoutFormWatch?.(
    "customer.shipping_address.country",
  );

  const billingFieldPath =
    "customer.payment_method.billing_address" as Path<CheckoutFormData>;
  const {
    stateOptions: billingStateOptions,
    addressFields: billingAddressFields,
    handleCountryChange: billingHandleCountryChange,
  } = useCartCountry(
    cartToken,
    paymentMethodsIncludes ?? null,
    query?.data?.cart?.country?.iso,
    countries,
  );

  // wrapper for setValue to work with useAutocomplete
  const setValueWrapper = useCallback(
    <T extends CheckoutFormData>(name: Path<T>, value: string) => {
      if (checkoutFormSetValue) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        checkoutFormSetValue(name as any, value);
      }
    },
    [checkoutFormSetValue],
  ) as UseFormSetValue<CheckoutFormData>;

  const {
    renderAutocompleteOptions: billingRenderAutocompleteOptions,
    getAutocompleteOptions: billingGetAutocompleteOptions,
    handleOptionClick: billingHandleOptionClick,
  } = useAutocomplete(
    setValueWrapper,
    `${billingFieldPath}.city` as Path<CheckoutFormData>,
    `${billingFieldPath}.state` as Path<CheckoutFormData>,
    `${billingFieldPath}.postal_code` as Path<CheckoutFormData>,
    `${billingFieldPath}.address1` as Path<CheckoutFormData>,
    billingStateOptions,
  );

  const onBillingCountryChange = useCallback(
    (countryCode: string, countryName: string) => {
      if (!checkoutFormSetValue) return;

      const resetData = createAddressCountryReset(countryCode, countryName);

      Object.entries(resetData).forEach(([key, value]) => {
        checkoutFormSetValue(
          `${billingFieldPath}.${key}` as Path<CheckoutFormData>,
          value as string,
        );
      });
      // Don't update cart country
      billingHandleCountryChange(countryCode, false);
    },
    [checkoutFormSetValue, billingFieldPath, billingHandleCountryChange],
  );

  const {
    stateOptions: ccStateOptions,
    handleCountryChange: ccHandleCountryChange,
    addressFields: ccAddressFields,
  } = useCartCountry(cartToken || "", null, "US", countries);

  const ccResolver = useDynamicAddressResolver<CreditCardFormData>(
    ccAddressFields,
    (fields) =>
      createCreditCardFormDataSchema(
        {
          firstNameRequired: t("first_name_is_required"),
          lastNameRequired: t("last_name_is_required"),
          cardNumberRequired: t("card_number_is_required"),
          expirationDateFormat: t("please_enter_valid_expiration_date"),
          cvcMinDigits: t("cvc_minimum_digits"),
          addressLine1Required: t("address_line_1_is_required"),
          cityRequired: t("city_is_required"),
          stateRequired: t("state_is_required"),
          postalCodeRequired: t("postal_code_is_required"),
          countryRequired: t("country_is_required"),
          phoneRequired: t("address_phone_number_is_required"),
        },
        fields,
      ),
  );

  const { control, reset, setValue, watch, trigger } =
    useForm<CreditCardFormData>({
      resolver: ccResolver,
      defaultValues: {
        card_number: "",
        expiration_date: "",
        cvc: "",
        first_name: "",
        last_name: "",
        default: false,
        use_shipping_address_for_billing: true,
        billing_address: {
          address1: "",
          address2: "",
          city: "",
          state: "",
          postal_code: "",
          country: "US",
        },
      },
    });

  const isCartQueryFetching = query.isFetching;
  const isAddCreditCardButtonDisabled = isDisabled || isCartQueryFetching;

  const addCreditCardToCustomerMutation = useAddCreditCardMutation({
    onSuccess: async (response, { paymentMethodData }) => {
      // billing_address is already part of paymentMethodData; reuse it to
      // hydrate the dropdown selection without a second fetch.
      const billingAddress = paymentMethodData.billing_address;
      const billingAddressForSelect: CustomerPaymentMethod["billing_address"] =
        billingAddress
          ? {
              name: billingAddress.name ?? null,
              address1: billingAddress.address1 ?? null,
              address2: billingAddress.address2 ?? null,
              city: billingAddress.city ?? null,
              state: billingAddress.state ?? null,
              zip: billingAddress.zip ?? null,
              country_code: billingAddress.country_code ?? null,
              phone: billingAddress.phone ?? null,
            }
          : undefined;
      await handleSelect({
        ...response.payment_method,
        details: { ...response.payment_method.details },
        billing_address: billingAddressForSelect,
      });
      await queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
      // VGS tokenization is async; the first refetch can land before
      // vgs_card_id is populated, which would hide the new card from the
      // filtered list. Re-invalidate a few more times to pick up the
      // final tokenized card.
      const retryInvalidate = (delayMs: number) =>
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
        }, delayMs);
      retryInvalidate(3000);
      retryInvalidate(8000);
      retryInvalidate(15000);
    },
    onError: (error) => {
      console.error("Error creating credit card:", error);
      setSaveCardError(t("unable_to_add_credit_card"));
    },
  });

  const handleClose = useCallback(
    (isOpen: boolean = false) => {
      if (isOpen) {
        setOpenCreditCardDialog(true);
      } else {
        setOpenCreditCardDialog(false);
        reset();
        setSaveCardError(undefined);
        resetVgsRef.current();
      }
    },
    [reset],
  );

  const handleVgsError = useCallback((error: string) => {
    setSaveCardError(error);
    setIsSubmitting(false);
  }, []);

  const handleVgsSuccess = useCallback(
    async (response: VgsCollectSubmitResponse) => {
      try {
        const expValue = response?.card_exp;
        if (!expValue) {
          throw new Error("Missing expiration date");
        }

        const cleanExp = expValue.replace(/\s+/g, "");
        const [expMonth, expYearShort] = cleanExp.split("/");

        if (!expMonth || !expYearShort) {
          throw new Error("Invalid expiration date format");
        }

        const expYear =
          expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

        const billingAddress = watch("billing_address");
        const cardHolder = response?.card_holder || "";

        if (!cardHolder || cardHolder.trim().length === 0) {
          throw new Error("Cardholder name is required");
        }

        const isDefault = watch("default");
        const isFirstCustomerPaymentMethod =
          customerPaymentMethods.length === 0;

        const paymentAccountId = getPaymentAccountId;

        const paymentMethodData = {
          type: "credit_card" as const,
          country_code: billingAddress.country || "US",
          default_payment_method: isDefault || isFirstCustomerPaymentMethod,
          ...(paymentAccountId && { payment_account: paymentAccountId }),
          billing_address: {
            name: cardHolder,
            address1: billingAddress.address1 || "",
            address2: billingAddress.address2 || "",
            city: billingAddress.city || "",
            state: billingAddress.state || "",
            zip: billingAddress.postal_code || "",
            country_code: billingAddress.country,
            phone: (billingAddress as { phone?: string }).phone || undefined,
          },
          payment_method: {
            token: response.card_number,
            token_provider: "vgs" as const,
            exp_month: expMonth,
            exp_year: expYear,
            card_holder: cardHolder,
            cvv_token: response.card_cvc,
          },
        };

        await addCreditCardToCustomerMutation.mutateAsync({
          paymentMethodData,
        });

        handleClose(false);
        setSaveCardError(undefined);
      } catch (error) {
        console.error("VGS submission error:", error);
        if (isApiError(error)) {
          setSaveCardError(
            sanitizeErrorMessage(
              error?.data?.errors?.payment?.[0] || error?.data?.error_message,
            ),
          );
        } else {
          setSaveCardError(
            sanitizeErrorMessage(
              error instanceof Error ? error.message : undefined,
            ),
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      watch,
      getPaymentAccountId,
      addCreditCardToCustomerMutation,
      handleClose,
      customerPaymentMethods.length,
    ],
  );

  const {
    hasActiveSession,
    isFormReady: isVgsFormReady,
    iframeLoadError: vgsIframeLoadError,
    submitCard: submitVgsCard,
    vgsFieldState,
    resetVgs: resetVgsForm,
  } = useVgsPayment({
    cartToken,
    selectedPaymentType: "credit_card",
    paymentOptions: query.data?.cart?.available_payment_methods || [],
    onSuccess: handleVgsSuccess,
    onError: handleVgsError,
    enabled: openCreditCardDialog,
  });

  useEffect(() => {
    resetVgsRef.current = resetVgsForm;
  }, [resetVgsForm]);

  // STALE SESSION TIMEOUT — mirrors PaymentMethodSelect behaviour.
  // If the credit-card dialog stays open past the VGS session lifetime,
  // tear down the expired session so the user isn't left with a dead form.
  useEffect(() => {
    if (!hasActiveSession || !resetVgsForm) {
      return;
    }

    const timer = setTimeout(() => {
      resetVgsForm();
      setOpenCreditCardDialog(false);
      setSaveCardError(undefined);
    }, VGS_SESSION_TIMEOUT);

    return () => clearTimeout(timer);
  }, [hasActiveSession, resetVgsForm]);

  const deleteMutation = useDeletePaymentMethodMutation<{
    previousPaymentMethods: CustomerPaymentMethodsResponse | undefined;
  }>({
    onMutate: async (paymentMethodId) => {
      await queryClient.cancelQueries({ queryKey: paymentMethodsQueryKey });

      const previousPaymentMethods =
        queryClient.getQueryData<CustomerPaymentMethodsResponse>(
          paymentMethodsQueryKey,
        );

      if (previousPaymentMethods?.payment_methods) {
        queryClient.setQueryData<CustomerPaymentMethodsResponse>(
          paymentMethodsQueryKey,
          (oldData) => {
            if (!oldData?.payment_methods) {
              return oldData;
            }

            return {
              ...oldData,
              payment_methods: oldData.payment_methods.filter(
                (paymentMethod: CustomerPaymentMethod) =>
                  paymentMethod.id !== Number(paymentMethodId),
              ),
            };
          },
        );
      }

      return { previousPaymentMethods };
    },
    onError: (error, _variables, context) => {
      if (context?.previousPaymentMethods) {
        queryClient.setQueryData(
          paymentMethodsQueryKey,
          context.previousPaymentMethods,
        );
      }
      console.error("Error deleting credit card:", error);
      setDeleteCardError(t("unable_to_delete_credit_card"));
    },
    onSuccess: async () => {
      setDeleteCardError(undefined);
      await queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
    },
  });

  const updatePaymentMethodMutation = useUpdatePaymentMethodMutation({
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
      const billingAddress = variables.body?.billing_address;
      const isUpdatedMethodSelected =
        selectedPaymentMethodId !== null &&
        Number(variables.paymentMethodId) === selectedPaymentMethodId &&
        billingAddress;
      if (isUpdatedMethodSelected && cartToken) {
        const { name, address1, address2, city, state, zip, country_code } =
          billingAddress;
        try {
          const updatedCart = await updateAddress(
            {
              address: {
                name: name ?? "",
                address1: address1 ?? "",
                address2: address2 ?? "",
                city: city ?? "",
                state: state ?? "",
                postal_code: zip ?? "",
                phone: (billingAddress as { phone?: string }).phone,
                country_code: country_code ?? "",
              },
              type: "bill_to",
            },
            cartToken,
          );
          queryClient.setQueryData(
            ["cart", cartToken, paymentMethodsIncludes],
            updatedCart,
          );
        } catch (err) {
          console.error(
            "Failed to sync cart billing address after payment method update:",
            err,
          );
        }
      }
      handleCloseEditDialog();
    },
    onError: (error) => {
      console.error("Error updating payment method:", error);
      if (isApiError(error)) {
        setEditError(
          sanitizeErrorMessage(
            (error as ApiError)?.data?.error_message,
            "Failed to update payment method. Please try again.",
          ),
        );
      } else {
        setEditError("An unexpected error occurred. Please try again.");
      }
    },
  });

  const setDefaultMutation = useUpdatePaymentMethodMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: paymentMethodsQueryKey });
    },
  });

  if (
    selectedAlternatePaymentMethod &&
    !alternatePaymentMethods?.some(
      (method) => method.type === selectedAlternatePaymentMethod.type,
    )
  ) {
    setSelectedAlternatePaymentMethod(null);
  }

  const onSubmit = async () => {
    setIsSubmitting(true);
    setSaveCardError(undefined);

    const isSchemaValid = await trigger("billing_address");

    if (!isSchemaValid) {
      setIsSubmitting(false);
      return;
    }

    try {
      await submitVgsCard();
    } catch (error) {
      console.error("Error:", error);
      setSaveCardError("Failed to save card");
      setIsSubmitting(false);
    }
  };

  const handleDeleteCard = async (cardId: number | null) => {
    if (!cardId) return;
    await deleteMutation.mutateAsync(cardId.toString());
    setOpenDeleteDialog(false);
    setSelectedCreditCard(null);
  };

  const handleSelectPaymentMethod = async (
    paymentMethod: CustomerPaymentMethod,
  ) => {
    if (isRadioDisabled) return;
    lastSyncedCardIdRef.current = null;
    setOptimisticSelectedCardId(paymentMethod.id);
    try {
      await handleSelect(paymentMethod);
    } catch {
      // Parent mutation rolled the cart back and set a specific error
      // message on paymentError (which renders in the checkout error
      // section). Clear optimistic so the radio snaps back to the cart's
      // actual payment_method — otherwise the UI would lie about which
      // card is selected vs what Pay Now would submit.
      setOptimisticSelectedCardId(null);
    }
  };

  const handleSelectAlternatePayment = (
    paymentMethod: AvailablePaymentMethod,
  ) => {
    if (isDisabled) return;
    setSelectedAlternatePaymentMethod(paymentMethod);
    onCustomerDetailsChange?.(null);
  };

  const handleCustomerDetailsSubmit = (details: Record<string, string>) => {
    onCustomerDetailsChange?.(details);
  };

  const handleViewPaymentMethod = (paymentMethod: CustomerPaymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    requestAnimationFrame(() => {
      setIsViewOpen(true);
    });
  };

  const handleCloseViewDialog = () => {
    setIsViewOpen(false);
    setSelectedPaymentMethod(null);
  };

  const handleOpenEditFromView = () => {
    setIsViewOpen(false);
    setEditError(undefined);
    requestAnimationFrame(() => {
      setIsEditOpen(true);
    });
  };

  const handleCloseEditDialog = () => {
    setIsEditOpen(false);
    setSelectedPaymentMethod(null);
    setEditError(undefined);
  };

  const handleEditSubmit = (data: EditPaymentMethodFormData) => {
    if (!selectedPaymentMethod) return;
    updatePaymentMethodMutation.mutate({
      paymentMethodId: selectedPaymentMethod.id.toString(),
      body: {
        set_as_default: data.set_as_default,
        billing_address: data.billing_address,
      },
    });
  };

  const getInitialValuesForPaymentMethod = (
    paymentMethod: AvailablePaymentMethod,
  ): Record<string, string> => {
    if (
      !customerDetailsInitialValues ||
      !paymentMethod.customer_details_schema
    ) {
      return {};
    }

    const filteredValues: Record<string, string> = {};
    Object.entries(customerDetailsInitialValues).forEach(([key, value]) => {
      if (paymentMethod.customer_details_schema?.[key] && value) {
        filteredValues[key] = String(value);
      }
    });
    return filteredValues;
  };

  return (
    <div className="w-full border-b border-gray-300 pb-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="w-full"
      >
        <AccordionItem value="payment-methods" className="border-b-0">
          <AccordionTrigger
            data-testid="toggle-payment-methods"
            className="mt-4 mb-1 flex w-full items-center justify-between py-1 text-left hover:no-underline"
          >
            <span className="text-sm text-gray-400">{t("payment_method")}</span>
          </AccordionTrigger>

          {networkError && (
            <div
              className="text-sm text-red-500"
              data-testid="payment-error-inline"
            >
              {networkError}
            </div>
          )}

          {!accordionValue && (
            <div
              className="flex cursor-pointer flex-row space-x-2"
              onClick={() => setAccordionValue("payment-methods")}
            >
              <RenderSelectedPaymentMethod
                defaultPaymentMethod={defaultPaymentMethod}
                customerPaymentMethodsLoading={customerPaymentMethodsLoading}
                customerPaymentMethods={customerPaymentMethods}
                selectedAlternatePaymentMethod={selectedAlternatePaymentMethod}
                selectedPaymentMethodId={effectiveSelectedCardId}
              />
            </div>
          )}

          <AccordionContent className="px-0.5 pt-2">
            {!showAlternatePaymentMethods ? (
              <>
                {!displayedPaymentMethods?.length && (
                  <div className="mt-2 mb-1 text-sm text-gray-400">
                    {t("no_payment_methods_found")}
                  </div>
                )}
                <div
                  className="mt-2 flex flex-col justify-between"
                  data-testid="payment-method-options"
                >
                  {displayedPaymentMethods?.map((paymentMethod, index) => {
                    const isLastItem =
                      index === displayedPaymentMethods.length - 1;
                    const isFirstItem = index === 0;
                    const isClosed = paymentMethod.au_status === "closed";
                    const isUpdateNeeded =
                      paymentMethod.au_status === "update_needed";
                    const paymentLogoUrl = resolvePaymentMethodLogoUrl({
                      logoUrl: paymentMethod.details.logo_url,
                      cardBrand: paymentMethod.details.card_brand,
                      cardType: paymentMethod.details.card_type,
                      detailsType: paymentMethod.details.type,
                      paymentTitle: paymentMethod.payment_title,
                      paymentType: paymentMethod.payment_type,
                      source: paymentMethod.source,
                    });
                    const paymentLabel = resolvePaymentMethodDisplayLabel({
                      lastFour: paymentMethod.details.last_four,
                      cardBrand: paymentMethod.details.card_brand,
                      cardType: paymentMethod.details.card_type,
                      detailsType: paymentMethod.details.type,
                      paymentTitle: paymentMethod.payment_title,
                      paymentType: paymentMethod.payment_type,
                      source: paymentMethod.source,
                    });
                    return (
                      <RadioInputBar
                        key={paymentMethod.id}
                        onSelect={() =>
                          handleSelectPaymentMethod(paymentMethod)
                        }
                        leftContent={
                          <div className="flex flex-row space-x-2">
                            {paymentLogoUrl && (
                              <img
                                src={paymentLogoUrl}
                                alt={"payment card"}
                                className="h-5 w-10 object-contain"
                              />
                            )}
                            <div className="flex flex-col">
                              {(paymentLabel || isClosed || isUpdateNeeded) && (
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                                  {paymentLabel && <span>{paymentLabel}</span>}
                                  {isClosed && (
                                    <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                      {t("card_closed", "Card Closed")}
                                    </span>
                                  )}
                                  {isUpdateNeeded && (
                                    <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                      {t("update_needed", "Update Needed")}
                                    </span>
                                  )}
                                </div>
                              )}
                              {isClosed && (
                                <p className="text-xs text-red-600">
                                  {t(
                                    "card_closed_helper",
                                    "This card is no longer valid. Please use a different one.",
                                  )}
                                </p>
                              )}
                              {isUpdateNeeded && (
                                <p className="text-xs text-amber-600">
                                  {t(
                                    "update_needed_helper",
                                    "Your card issuer recommends updating this card's details.",
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                        }
                        isSelected={
                          effectiveSelectedCardId === paymentMethod.id
                        }
                        name={paymentMethod.details.card_type ?? ""}
                        rightContent={
                          <div className="flex flex-row items-center space-x-2">
                            {(paymentMethod.default ||
                              defaultPaymentMethod?.id ===
                                paymentMethod.id) && (
                              <p className="mt-1 inline-flex w-fit items-center justify-center rounded bg-gray-500 px-2 py-0.5 text-xs font-medium text-white">
                                {t("default")}
                              </p>
                            )}
                            <EllipsesDropdown
                              onEdit={() =>
                                handleViewPaymentMethod(paymentMethod)
                              }
                              editLabel={t("edit")}
                              onDelete={() => {
                                setSelectedCreditCard(paymentMethod);
                                requestAnimationFrame(() => {
                                  setOpenDeleteDialog(true);
                                });
                              }}
                              deleteLabel={t("delete")}
                              onMakeDefault={
                                paymentMethod.default ||
                                defaultPaymentMethod?.id === paymentMethod.id
                                  ? undefined
                                  : () =>
                                      setDefaultMutation.mutate({
                                        paymentMethodId:
                                          paymentMethod.id.toString(),
                                        body: { set_as_default: true },
                                      })
                              }
                              makeDefaultLabel={t("set_as_default")}
                              disabled={isDisabled}
                            />
                          </div>
                        }
                        disabled={isRadioDisabled || isClosed}
                        className={cn(
                          "cursor-pointer items-center bg-gray-100/40 px-3",
                          isLastItem && "rounded-b",
                          isFirstItem && "rounded-t",
                          {
                            "bg-white opacity-100":
                              effectiveSelectedCardId === paymentMethod.id,
                            "cursor-not-allowed opacity-50":
                              isRadioDisabled || isClosed,
                          },
                        )}
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="mt-2 flex flex-col justify-between">
                {alternatePaymentMethods?.map((paymentMethod, index) => {
                  const isLastItem =
                    index === alternatePaymentMethods.length - 1;
                  const isFirstItem = index === 0;
                  return (
                    <div key={paymentMethod.id}>
                      <RadioInputBar
                        onSelect={() =>
                          handleSelectAlternatePayment(paymentMethod)
                        }
                        leftContent={
                          <div className="flex flex-row space-x-2">
                            <div className="text-sm font-medium text-gray-900">
                              {paymentMethod.name}
                            </div>
                          </div>
                        }
                        rightContent={
                          <AlternatePaymentIcons
                            paymentMethod={paymentMethod}
                          />
                        }
                        isSelected={
                          selectedAlternatePaymentMethod?.name ===
                          paymentMethod.name
                        }
                        name={paymentMethod.name}
                        disabled={isDisabled}
                        className={cn(
                          selectedAlternatePaymentMethod?.name ===
                            paymentMethod.name
                            ? "my-0.5 rounded-b-none bg-white"
                            : "bg-gray-100/40",
                          "cursor-pointer items-center px-3",
                          isFirstItem && "rounded-t",
                          isLastItem && "rounded-b",
                          isDisabled && "cursor-not-allowed opacity-50",
                        )}
                      />
                      {selectedAlternatePaymentMethod?.name ===
                        paymentMethod.name &&
                      ((paymentMethod.customer_details_schema &&
                        Object.keys(paymentMethod.customer_details_schema)
                          .length > 0) ||
                        (paymentMethod.collect_billing_address &&
                          checkoutFormControl &&
                          checkoutFormWatch &&
                          checkoutFormSetValue &&
                          checkoutFormClearErrors &&
                          countries)) ? (
                        <div
                          className={cn(
                            "bg-white shadow-xs ring-1 ring-gray-300",
                            isLastItem && "rounded-b",
                          )}
                        >
                          {paymentMethod.customer_details_schema &&
                          Object.keys(paymentMethod.customer_details_schema)
                            .length > 0 ? (
                            <CustomerDetailsForm
                              paymentMethod={paymentMethod}
                              onSubmit={handleCustomerDetailsSubmit}
                              validationErrors={customerDetailsValidationErrors}
                              onClearFieldError={
                                onClearCustomerDetailsFieldError
                              }
                              initialValues={getInitialValuesForPaymentMethod(
                                paymentMethod,
                              )}
                            />
                          ) : null}
                          {paymentMethod.collect_billing_address &&
                            !query?.data?.cart?.digital_only &&
                            checkoutFormControl &&
                            checkoutFormWatch &&
                            checkoutFormSetValue &&
                            checkoutFormClearErrors &&
                            countries && (
                              <>
                                <div className="h-input flex w-full items-center px-4 ring-1 ring-gray-300">
                                  <Checkbox
                                    control={checkoutFormControl}
                                    name="customer.payment_method.use_shipping_address_for_billing"
                                    label={t(
                                      "use_shipping_address_for_billing",
                                    )}
                                    id="use-shipping-address-for-billing-logged-in"
                                    onChange={() => {
                                      const newValue = !useShippingForBilling;
                                      checkoutFormSetValue(
                                        "customer.payment_method.use_shipping_address_for_billing" as Path<CheckoutFormData>,
                                        newValue,
                                      );
                                      checkoutFormClearErrors(
                                        "customer.payment_method.billing_address" as Path<CheckoutFormData>,
                                      );

                                      // pre-populate billing country from shipping when unchecking
                                      if (!newValue && shippingCountryCode) {
                                        checkoutFormSetValue(
                                          `${billingFieldPath}.country_code` as Path<CheckoutFormData>,
                                          shippingCountryCode,
                                        );
                                        checkoutFormSetValue(
                                          `${billingFieldPath}.country` as Path<CheckoutFormData>,
                                          shippingCountryName || "",
                                        );
                                        billingHandleCountryChange(
                                          shippingCountryCode,
                                          false,
                                        );
                                      }
                                    }}
                                  />
                                </div>
                                {!useShippingForBilling && (
                                  <AddressForm
                                    control={checkoutFormControl}
                                    countries={countries}
                                    watch={checkoutFormWatch}
                                    fieldPath={billingFieldPath}
                                    stateOptions={billingStateOptions}
                                    addressFields={billingAddressFields}
                                    onCountryChange={onBillingCountryChange}
                                    renderAutocompleteOptions={
                                      billingRenderAutocompleteOptions
                                    }
                                    getAutocompleteOptions={
                                      billingGetAutocompleteOptions
                                    }
                                    handleOptionClick={billingHandleOptionClick}
                                    isLoading={query.isLoading}
                                    roundedTop={false}
                                    roundedBottom={false}
                                    className="mt-[-7px] mb-[-7px]"
                                    collectName={false}
                                    clearErrors={checkoutFormClearErrors}
                                  />
                                )}
                              </>
                            )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex flex-col space-y-2">
        {!showAlternatePaymentMethods && hasCreditCardOption && (
          <button
            className={cn(
              "mt-3 text-left text-sm text-gray-900",
              isAddCreditCardButtonDisabled && "cursor-not-allowed opacity-50",
            )}
            onClick={() => {
              if (isAddCreditCardButtonDisabled) return;
              const shipTo = query.data?.cart?.ship_to;
              if (shipTo) {
                setValue("use_shipping_address_for_billing", true);
                setValue("billing_address.address1", shipTo.address1 || "");
                setValue("billing_address.address2", shipTo.address2 || "");
                setValue("billing_address.city", shipTo.city || "");
                setValue("billing_address.state", shipTo.state || "");
                setValue(
                  "billing_address.postal_code",
                  shipTo.postal_code || "",
                );
                setValue(
                  "billing_address.country",
                  shipTo.country_code || "US",
                );
                ccHandleCountryChange(shipTo.country_code || "US", false);
              } else {
                setValue("use_shipping_address_for_billing", false);
              }
              setOpenCreditCardDialog(true);
            }}
            disabled={isAddCreditCardButtonDisabled}
          >
            + {t("add_credit_card")}
          </button>
        )}

        {alternatePaymentMethods?.length && hasCreditCardOption ? (
          <button
            className={cn(
              "mt-3 text-left text-sm text-gray-900 underline",
              isDisabled && "cursor-not-allowed opacity-50",
            )}
            disabled={isDisabled}
            onClick={() => {
              if (isDisabled) return;
              if (!showAlternatePaymentMethods) {
                setShowAlternatePaymentMethods(true);
                setSelectedAlternatePaymentMethod(
                  alternatePaymentMethods?.[0] ?? null,
                );
              } else {
                setShowAlternatePaymentMethods(false);
                setSelectedAlternatePaymentMethod(null);
                lastSyncedCardIdRef.current = null;
              }
            }}
          >
            {!showAlternatePaymentMethods
              ? t("use_alternative_payment_method")
              : t("use_credit_card")}
          </button>
        ) : null}

        <DropZone
          zone="below_payment_method"
          token={cartToken}
          subdomain={subdomain ?? null}
          page="checkout"
          companyId={query.data?.cart?.company?.id}
        />
      </div>
      <DeleteDialog
        title={t("delete_payment_method")}
        description={t("delete_payment_method_message")}
        onAction={() => handleDeleteCard(selectedCreditCard?.id ?? null)}
        actionText={t("delete")}
        openDialog={openDeleteDialog}
        setOpenDialog={setOpenDeleteDialog}
        errorMsg={deleteCardError}
        isLoading={deleteMutation.isPending}
      />
      <CreditCardDialog
        isOpen={openCreditCardDialog}
        control={control}
        onSubmit={onSubmit}
        handleClose={handleClose}
        isSubmitting={isSubmitting}
        setValue={setValue}
        saveCardError={saveCardError}
        query={query}
        cartToken={cartToken}
        isVgsFormReady={isVgsFormReady}
        iframeLoadError={vgsIframeLoadError}
        vgsFieldState={vgsFieldState}
        addressFields={ccAddressFields}
        stateOptions={ccStateOptions}
        handleCountryChange={ccHandleCountryChange}
        countries={countries}
        isTestGateway={creditCardOption?.test ?? false}
      />
      <ViewPaymentMethodDialog
        isOpen={isViewOpen}
        paymentMethod={selectedPaymentMethod}
        onClose={handleCloseViewDialog}
        onEdit={handleOpenEditFromView}
      />

      <EditPaymentMethodDialog
        isOpen={isEditOpen}
        paymentMethod={selectedPaymentMethod}
        onClose={handleCloseEditDialog}
        onSubmit={handleEditSubmit}
        isSubmitting={updatePaymentMethodMutation.isPending}
        error={editError}
        countries={countries}
        cartToken={cartToken}
      />
    </div>
  );
}
